import * as React from "react";
import get from "lodash/get";

import "./MyRewards.css";
import { isEditMode } from "../../utils/sendClickToMerchant";
import { ComponentBase, ComponentBaseProps } from "../ComponentBase";
import { RewardRecord } from "../../reducers/reward/rewards";
import { currencySymbols } from "../../utils/currencies";
import { copyText } from "../actions/copyText";
import {
  getCoupons,
  getRewards,
  isGetRewardsLoading,
} from "../../reducers/reward/methods";
import { CouponRecord } from "../../reducers/reward/coupons";

const rewardNameFromExchangeOrCouponBank = (reward: RewardRecord): string => {
  const exchangerName =
    get(reward, "transaction.exchangerName") || get(reward, "earningEventName");

  return exchangerName || nameFromCouponBankOrRewardDefinition(reward);
};

export const nameFromCouponBankOrRewardDefinition = (
  reward: RewardRecord
): string => {
  const customerFacingName = get(reward, "couponBank.customerFacingName");

  if (customerFacingName) {
    return customerFacingName;
  }

  const currency =
    get(reward, "couponBank.currency") ||
    get(reward, "definition.currency") ||
    get(reward, "reward.currency");
  const symbol = currencySymbols[currency] || "";
  const amount =
    get(reward, "couponBank.amount") ||
    get(reward, "definition.amount") ||
    get(reward, "reward.amount");

  if (symbol === "%") {
    return `${amount}${symbol} Coupon`;
  }

  return `${symbol}${amount} Coupon`;
};

export const validReward = (
  reward: RewardRecord,
  coupons: CouponRecord[]
): boolean => {
  const couponType = get(reward, "reward.rewardType") === "discount";
  const couponCode = get(reward, "coupon.id") || get(reward, "definition.code");
  if (couponCode && couponType) {
    const coupon = coupons.find(
      (coupon: CouponRecord) =>
        coupon.coupon.localeCompare(couponCode, undefined, {
          sensitivity: "accent",
        }) === 0
    );

    // if we have a record of the coupon, confirm its status
    return !!coupon && coupon.status === "distributed";
  }

  return false;
};

const getRewardSortTimestamp = (reward: RewardRecord): number => {
  const anyReward = reward as any;
  const dateString: string | undefined =
    anyReward.issuedOn || anyReward.createdOn;

  if (!dateString) {
    return 0;
  }

  const timestamp = Date.parse(dateString);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRewardsNewestFirst = (rewards: RewardRecord[]): RewardRecord[] => {
  if (!rewards || rewards.length === 0) {
    return rewards;
  }

  return [...rewards].sort(
    (a, b) => getRewardSortTimestamp(b) - getRewardSortTimestamp(a)
  );
};

const exampleRewards: RewardRecord[] = [
  {
    id: "00000000-0000-0000-0000-000000000000",
    createdOn: "2021-12-16T01:45:37.362Z",
    coupon: {
      id: "FB45cf2H03s",
      status: "distributed",
    },
    couponBank: {
      amount: 10,
      currency: "percent",
    },
    reward: {
      rewardType: "discount",
    },
    transaction: {
      exchangerName: "10% Off",
    },
  },
  {
    id: "00000000-0000-0000-0000-000000000002",
    createdOn: "2021-12-16T01:45:37.362Z",
    coupon: {
      id: "FB32df2H94fFB32df2H94fFB32df2H94f",
      status: "distributed",
    },
    couponBank: {
      amount: 20,
      currency: "USD",
    },
    reward: {
      rewardType: "discount",
    },
  },
  {
    id: "00000000-0000-0000-0000-000000000001",
    createdOn: "2021-12-16T01:45:37.362Z",
    coupon: {
      id: "FB84c3Jd9ds",
      status: "distributed",
    },
    couponBank: {
      amount: 25,
      currency: "USD",
    },
    reward: {
      rewardType: "discount",
    },
    transaction: {
      exchangerName: "Free Shipping",
    },
  },
  {
    id: "00000000-0000-0000-0000-000000000003",
    createdOn: "2021-12-16T01:45:37.362Z",
    coupon: {
      id: "coupthree",
      status: "distributed",
    },
    couponBank: {
      amount: 25,
      currency: "USD",
    },
  },
  {
    id: "00000000-0000-0000-0000-000000000004",
    createdOn: "2021-12-16T01:45:37.362Z",
    coupon: {
      id: "FB2dijDFAdkf",
      status: "distributed",
    },
    couponBank: {
      amount: 25,
      currency: "USD",
    },
    reward: {
      rewardType: "giftCard",
    },
  },
];

const exampleCoupons: CouponRecord[] = [
  {
    coupon: "FB45cf2H03s",
    status: "distributed",
  },
  {
    coupon: "FB84c3Jd9ds",
    status: "distributed",
  },
  {
    coupon: "coupthree",
    status: "redeemed",
  },
  {
    coupon: "FB32df2H94fFB32df2H94fFB32df2H94f",
    status: "distributed",
  },
  {
    coupon: "FB2dijDFAdkf",
    status: "distributed",
  },
];

export interface MyRewardsProps extends ComponentBaseProps {
  classNames: string;
  configName: string;
  coupons: ReturnType<typeof getCoupons>;
  isLoading: ReturnType<typeof isGetRewardsLoading>;
  name: string;
  rewards: ReturnType<typeof getRewards>;
  align?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  borderStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  copyButtonText?: string;
  couponCodeStyle?: React.CSSProperties;
  dividerStyle?: React.CSSProperties;
  notAuthenticatedStyle?: React.CSSProperties;
  notAuthenticatedText?: string;
  noRewardsText?: string;
  noRewardsStyle?: React.CSSProperties;
  rewardNameStyle?: React.CSSProperties;
  rowStyle?: React.CSSProperties;
  selectedIndex?: number;
  style?: React.CSSProperties;
  tableStyle?: React.CSSProperties;
  testMode?: string;
}

interface OnClickArgs {
  textToCopy: string;
  index: number;
}

interface CopyButtonProps {
  buttonStyle?: React.CSSProperties;
  copiedText?: string;
  copyButtonText?: string;
  couponCode: string;
  index: number;
  onClick: (args: OnClickArgs) => void;
  selectedIndex?: number;
}

class CopyButton extends React.Component<CopyButtonProps> {
  handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    this.props.onClick({
      textToCopy: this.props.couponCode,
      index: this.props.index,
    });
  };

  wasClicked = () => {
    return this.props.index === this.props.selectedIndex;
  };

  render() {
    return (
      <div
        onClick={this.handleClick}
        style={{
          cursor: "pointer",
          display: "inline",
          ...this.props.buttonStyle,
        }}
      >
        {this.wasClicked() ? this.props.copiedText : this.props.copyButtonText}
      </div>
    );
  }
}

export class MyRewards extends ComponentBase<MyRewardsProps> {
  renderNoRewards = () => {
    return (
      <div style={this.props.noRewardsStyle}>
        {this.props.noRewardsText || "Earn points to redeem them for rewards!"}
      </div>
    );
  };

  renderNotAuthenticated = () => {
    return (
      <div style={this.props.notAuthenticatedStyle}>
        {this.props.notAuthenticatedText || ""}
      </div>
    );
  };

  onClickCopyText = (args: { textToCopy: string; index: number }) => {
    const { index, textToCopy } = args;
    copyText({
      text: textToCopy,
    });

    this.setState({
      tempTextReplace: get(this.props, "config.settings.copiedText", "Copied!"),
    });
    this.setState({
      selectedIndex: index,
    });
    setTimeout(() => this.setState({ selectedIndex: undefined }), 1000);
    setTimeout(() => this.setState({ tempTextReplace: undefined }), 1000);
  };

  getContentTestMode = (filteredRewards: RewardRecord[], testMode?: string) => {
    switch (testMode) {
      case "notAuthenticated":
        return this.renderNotAuthenticated();
      case "noRewards":
        return this.renderNoRewards();
      case "MyRewards":
        return this.renderMyRewards(filteredRewards);
      default:
        return this.renderMyRewards(filteredRewards);
    }
  };

  getContent = (
    filteredRewards: RewardRecord[],
    isCustomerAuthenticated: boolean
  ) => {
    if (isCustomerAuthenticated) {
      if (filteredRewards.length === 0) {
        return this.renderNoRewards();
      }

      return this.renderMyRewards(filteredRewards);
    } else {
      return this.renderNotAuthenticated();
    }
  };

  getCouponCode = (reward: RewardRecord): string => {
    return get(reward, "coupon.id") || get(reward, "definition.code", "");
  };

  renderMyRewards = (filteredRewards: RewardRecord[]) => {
    const {
      buttonStyle,
      classNames,
      couponCodeStyle,
      dividerStyle,
      rewardNameStyle,
      rowStyle,
      tableStyle,
    } = this.props;

    return (
      <table
        className={`MyRewards-container ${classNames || ""}`}
        style={{
          ...tableStyle,
          borderCollapse: "collapse",
        }}
        aria-label="My Rewards"
        cellPadding={0}
        cellSpacing={0}
      >
        <tbody>
          {filteredRewards.map((record, index) => (
            <tr
              key={`${record.id}`}
              style={
                // no border for the last item
                index + 1 === filteredRewards.length
                  ? { ...dividerStyle, border: "none" }
                  : { ...dividerStyle }
              }
            >
              <td style={{ ...rewardNameStyle, ...rowStyle }}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {rewardNameFromExchangeOrCouponBank(record)}
                </div>
              </td>
              <td style={{ ...couponCodeStyle, ...rowStyle }}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {this.getCouponCode(record)}
                </div>
              </td>
              <td
                style={{
                  ...rowStyle,
                  textAlign: "right",
                }}
              >
                <CopyButton
                  buttonStyle={buttonStyle}
                  copiedText={this.state.tempTextReplace}
                  copyButtonText={this.props.copyButtonText}
                  couponCode={this.getCouponCode(record)}
                  index={index}
                  onClick={this.onClickCopyText}
                  selectedIndex={this.state.selectedIndex}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  render() {
    const { data, isEditing, isLoading, style, testMode } = this.props;

    const rewards = isEditMode() ? exampleRewards : this.props.rewards || [];
    const coupons = isEditMode() ? exampleCoupons : this.props.coupons || [];
    const filteredRewards = rewards.filter((reward: RewardRecord) =>
      validReward(reward, coupons)
    );
    const sortedRewards = sortRewardsNewestFirst(filteredRewards);

    const isCustomerAuthenticated = get(data, "customer.authenticated", false);

    if (isEditing) {
      return (
        <div
          style={{ ...style }}
          id={this.props.configName}
          onDragStart={this.onDragStart}
          onMouseDown={this.onMouseDown}
          ref={(component) => (this.component = component)}
          className={`MyRewards-div-container`}
        >
          {this.getContentTestMode(sortedRewards, testMode)}
        </div>
      );
    }

    if (isLoading) {
      return "...";
    }

    return (
      <div
        style={{ ...style }}
        id={this.props.configName}
        onDragStart={this.onDragStart}
        onMouseDown={this.onMouseDown}
        ref={(component) => (this.component = component)}
        className={`MyRewards-div-container`}
      >
        {this.getContent(sortedRewards, isCustomerAuthenticated)}
      </div>
    );
  }
}
